import type { ReactNode } from 'react';

import type { MessageTarget } from '../../validation/message-routing.js';
import {
  NativeValidityStateKeys,
  type ValidationError,
  type ValidationErrorSource,
} from '../../validation/types.js';
import type {
  FormFieldMessageContextState,
  ValidationErrorEntry,
  ValidationHint,
} from '../types/formfield-messages.types.js';

export const enum FormFieldMessagesAction {
  ADDMESSAGE = 'addmessage',
  REMOVEMESSAGE = 'removemessage',
  DISABLEMESSAGE = 'disablemessage',
  SETISTOUCHED = 'setistouched',
  SETFORMCONTROLID = 'setformcontrolid',
  SETMESSAGETARGET = 'setmessagetarget',
  ADDMEMBERFORMCONTROLID = 'addmemberformcontrolid',
  REMOVEMEMBERFORMCONTROLID = 'removememberformcontrolid',
}

type AddMessageBasePayload = {
  /** Entry identity — used for dedup/replace and as the React key. */
  id: string;
  /**
   * Reveals the message immediately, bypassing the touched-gate. Only actions
   * that are meant to surface right away (e.g. `form.setError()`) set this.
   */
  showError?: boolean;
};

type AddErrorPayload = AddMessageBasePayload & {
  error: ValidationError;
  hint?: never;
};

type AddHintPayload = AddMessageBasePayload & {
  hint: ReactNode;
  /** Who is registering this hint — a Strato control (`'strato'`) or the consumer (`'custom'`). */
  source?: ValidationErrorSource;
  /**
   * Native input the hint belongs to. Errors don't need this — they carry
   * their own target(s) via `ValidationError.formControlId`, which
   * `updateValidity()` reads directly to decide which input(s) receive
   * `setCustomValidity()`.
   */
  controlId?: string;
  error?: never;
};

export type AddMessagePayload = AddErrorPayload | AddHintPayload;

export type FormFieldMessagesReducerAction =
  | {
      type: FormFieldMessagesAction.ADDMESSAGE;
      payload: AddMessagePayload;
    }
  | {
      type: FormFieldMessagesAction.REMOVEMESSAGE;
      payload: {
        id: string;
        /** Must match the `source` (for hints) or the error's `source` (for errors) used when adding, so a consumer `Item` can't remove an internal entry that coincidentally shares its id. */
        source?: ValidationErrorSource;
      };
    }
  | {
      type: FormFieldMessagesAction.DISABLEMESSAGE;
      payload: {
        isDisabled: boolean | undefined;
      };
    }
  | {
      type: FormFieldMessagesAction.SETISTOUCHED;
      payload: { isTouched: boolean; resetShowError?: boolean };
    }
  | {
      type: FormFieldMessagesAction.SETFORMCONTROLID;
      payload: { formControlId: string | undefined };
    }
  | {
      type: FormFieldMessagesAction.SETMESSAGETARGET;
      payload: { messageTarget: MessageTarget[] | undefined };
    }
  | {
      type: FormFieldMessagesAction.ADDMEMBERFORMCONTROLID;
      payload: { formControlId: string };
    }
  | {
      type: FormFieldMessagesAction.REMOVEMEMBERFORMCONTROLID;
      payload: { formControlId: string };
    };

type HTMLFormControlElement =
  | HTMLSelectElement
  | HTMLInputElement
  | HTMLTextAreaElement;

// All native ValidityState keys that the browser tracks itself, excluding 'customError'
// which we always set explicitly via setCustomValidity().
const BROWSER_NATIVE_VALIDITY_KEYS: ReadonlySet<string> = new Set(
  Object.entries(NativeValidityStateKeys)
    .filter(([key]) => key !== 'customError')
    .map(([, v]) => v.toString()),
);

function usesDefaultValidityKey(error: ValidationError): boolean {
  return error.validityStateKey.equals(NativeValidityStateKeys.customError);
}

/**
 * Messages registered by a Strato control (`'native'`/`'strato'`) and by the
 * consumer (`'custom'`) live in the same arrays but must not overwrite or
 * remove each other, even when they share an id — auto-rendered
 * `FormFieldMessages.Item`s mirror internal entries under the internal id.
 */
function isSameOwnershipClass(
  a: ValidationErrorSource | undefined,
  b: ValidationErrorSource | undefined,
): boolean {
  return ((a ?? 'custom') === 'custom') === ((b ?? 'custom') === 'custom');
}

function isSameError(a: ValidationError, b: ValidationError): boolean {
  return (
    a.message === b.message &&
    a.source === b.source &&
    a.validityStateKey.equals(b.validityStateKey)
  );
}

/**
 * Typeguard function that will check if the element is a form control.
 * @internal
 */
function isHTMLFormControlElement(
  element: null | HTMLElement,
): element is HTMLFormControlElement {
  return !!(element && 'setCustomValidity' in element);
}

/**
 * Set custom validity on the HTML form control element.
 * @internal
 */
function setHTMLElementValidity(id: string, isValid: boolean) {
  const element = document.getElementById(id);
  if (isHTMLFormControlElement(element)) {
    element.setCustomValidity(isValid ? '' : ' ');
  }
}

/**
 * Pick the ids that should receive native `setCustomValidity()` updates.
 * Grouped controls (Radio, ToggleButtonGroup) target each member input; single
 * controls target the logical form-control id.
 */
function pickNativeValidityTargets(
  state: FormFieldMessageContextState,
): string[] | undefined {
  if (state.memberFormControlIds && state.memberFormControlIds.length > 0) {
    return state.memberFormControlIds;
  }
  if (state.formControlId !== undefined) {
    return [state.formControlId];
  }
  return undefined;
}

/**
 * Whether `error` should count against the validity of `targetId`. An error
 * with no `formControlId` applies to every target of the field (matching a
 * single control's own error, or a grouped control's field-wide error);
 * otherwise it applies only to the specific input(s) it names — a single
 * `ValidationError` can target multiple inputs via `formControlId: string[]`
 * (e.g. one form-level error affecting both sides of a date range).
 */
function affectsTarget(error: ValidationError, targetId: string): boolean {
  const { formControlId } = error;
  if (formControlId === undefined) {
    return true;
  }
  return Array.isArray(formControlId)
    ? formControlId.includes(targetId)
    : formControlId === targetId;
}

// Only errors the browser has no native knowledge of should be reflected via
// setCustomValidity(). These are:
//   1. Any error not sourced from the browser's own native validity state
//      (developer-added or Strato-internal)
//   2. Strato-internal errors whose validityStateKey is not a browser-native key
//      (e.g. 'syntaxError', 'invalidOrder', 'customError', …)
//
// Browser-native errors (valueMissing, typeMismatch, patternMismatch, …) must NOT be
// passed to setCustomValidity() — the browser already tracks them, and adding a
// redundant customError would prevent checkValidity() from returning true once
// the native constraint is satisfied.
// 'customError' is excluded from BROWSER_NATIVE_VALIDITY_KEYS because it is only set
// by us via setCustomValidity, so it always requires our explicit call to maintain.
function needsExplicitValidity(error: ValidationError): boolean {
  return (
    error.source !== 'native' ||
    !BROWSER_NATIVE_VALIDITY_KEYS.has(error.validityStateKey.toString())
  );
}

/**
 * Function that will determine the validity of a form control.
 * @internal
 */
function updateValidity(state: FormFieldMessageContextState) {
  const { errors, isTouched, isDisabled } = state;

  const newIsValid = errors.length === 0;
  const newShowError =
    !isDisabled && (state.showError || (isTouched ?? false)) && !newIsValid;

  const targetControlIds = pickNativeValidityTargets(state);

  targetControlIds?.forEach((id) => {
    const isInvalidForTarget = errors.some(
      ({ error }) => needsExplicitValidity(error) && affectsTarget(error, id),
    );
    setHTMLElementValidity(id, !isInvalidForTarget);
  });

  return {
    ...state,
    isValid: newIsValid,
    showError: newShowError,
  };
}

function addHint(
  state: FormFieldMessageContextState,
  newState: FormFieldMessageContextState,
  payload: AddHintPayload,
): FormFieldMessageContextState {
  const entry: ValidationHint = {
    id: payload.id,
    message: payload.hint,
    ...(payload.controlId !== undefined && { controlId: payload.controlId }),
    ...(payload.source !== undefined && { source: payload.source }),
  };

  const existing = state.hints.find((hint) => hint.id === payload.id);

  if (existing && !isSameOwnershipClass(existing.source, payload.source)) {
    // An internal message already owns this id — a consumer `Item` rendered for
    // it must not overwrite the entry it is only mirroring.
    return state;
  }

  if (
    existing &&
    existing.message === entry.message &&
    existing.source === entry.source &&
    existing.controlId === entry.controlId
  ) {
    return state;
  }

  newState.hints = existing
    ? state.hints.map((hint) => (hint.id === payload.id ? entry : hint))
    : [...state.hints, entry];

  return updateValidity(newState);
}

function addInternalError(
  state: FormFieldMessageContextState,
  newState: FormFieldMessageContextState,
  payload: AddErrorPayload,
  entry: ValidationErrorEntry,
): FormFieldMessageContextState | undefined {
  if (!usesDefaultValidityKey(payload.error)) {
    // Browser validity errors are keyed by validityStateKey; only add once.
    const alreadyRegistered = state.errors.some(
      ({ error }) =>
        error.source !== 'custom' &&
        error.validityStateKey.equals(payload.error.validityStateKey),
    );
    if (alreadyRegistered) {
      return state;
    }
    newState.errors = [...state.errors, entry];
    return undefined;
  }

  const internalCustomErrors = state.errors.filter(
    ({ error }) => error.source !== 'custom' && usesDefaultValidityKey(error),
  );

  // If the same sole customError is already present and neither its content nor
  // showError changes, return early to keep the array reference stable and
  // prevent a useEffect re-run → re-dispatch → infinite-render loop.
  if (
    internalCustomErrors.length === 1 &&
    internalCustomErrors[0].id === payload.id
  ) {
    if (
      (payload.showError ?? state.showError) === state.showError &&
      isSameError(internalCustomErrors[0].error, payload.error)
    ) {
      return state;
    }
    newState.errors = state.errors.map((existing) =>
      existing.id === payload.id ? entry : existing,
    );
    return undefined;
  }

  // Replace all existing internal customErrors with the new one so only one
  // internal error is shown at a time.
  newState.errors = [
    ...state.errors.filter(
      ({ error }) =>
        !(error.source !== 'custom' && usesDefaultValidityKey(error)),
    ),
    entry,
  ];
  return undefined;
}

function addError(
  state: FormFieldMessageContextState,
  newState: FormFieldMessageContextState,
  payload: AddErrorPayload,
): FormFieldMessageContextState {
  const entry: ValidationErrorEntry = {
    id: payload.id,
    error: payload.error,
  };

  if (payload.error.source !== 'custom') {
    const earlyReturn = addInternalError(state, newState, payload, entry);
    if (earlyReturn) {
      return earlyReturn;
    }
  } else {
    const existing = state.errors.find(({ id }) => id === payload.id);

    if (
      existing &&
      !isSameOwnershipClass(existing.error.source, payload.error.source)
    ) {
      // An internal message already owns this id — leave it untouched.
      return state;
    }

    if (existing) {
      if (
        isSameError(existing.error, payload.error) &&
        (payload.showError ?? state.showError) === state.showError
      ) {
        return state;
      }
      newState.errors = state.errors.map((candidate) =>
        candidate.id === payload.id ? entry : candidate,
      );
    } else if (!state.hints.some((hint) => hint.id === payload.id)) {
      newState.errors = [...state.errors, entry];
    }
  }

  newState.showError = payload.showError ?? state.showError;

  return updateValidity(newState);
}

/** FormFieldMessage reducer function used for managing form field errors and hints. */
export function formFieldMessagesReducer(
  state: FormFieldMessageContextState,
  action: FormFieldMessagesReducerAction,
): FormFieldMessageContextState {
  const newState = { ...state };

  if (action.type === FormFieldMessagesAction.ADDMESSAGE) {
    const { payload } = action;

    return payload.error !== undefined
      ? addError(state, newState, { ...payload, error: payload.error })
      : addHint(state, newState, { ...payload, hint: payload.hint });
  }

  if (action.type === FormFieldMessagesAction.REMOVEMESSAGE) {
    const { payload } = action;

    const remainingErrors = state.errors.filter(
      (entry) =>
        !(
          entry.id === payload.id &&
          isSameOwnershipClass(entry.error.source, payload.source)
        ),
    );
    const remainingHints = state.hints.filter(
      (entry) =>
        !(
          entry.id === payload.id &&
          isSameOwnershipClass(entry.source, payload.source)
        ),
    );

    // avoid unnecessary updates and possible endless loops by early escaping if nothing was removed.
    if (
      remainingErrors.length === state.errors.length &&
      remainingHints.length === state.hints.length
    ) {
      return state;
    }

    newState.errors = remainingErrors;
    newState.hints = remainingHints;

    return updateValidity(newState);
  }

  if (action.type === FormFieldMessagesAction.DISABLEMESSAGE) {
    const { payload } = action;
    newState.isDisabled = payload.isDisabled;
  }

  if (action.type === FormFieldMessagesAction.SETISTOUCHED) {
    const { payload } = action;
    newState.isTouched = payload.isTouched;
    // A validation reset (resetValidation) must also suppress error display
    // immediately so hints re-appear in the same render pass rather than after
    // cleanup effects. Controls that merely un-touch on focus while editing
    // (e.g. FilterField) omit `resetShowError`, so a pending error stays shown
    // until the next validation cycle recomputes it — matching prior behavior.
    if (!payload.isTouched && payload.resetShowError) {
      newState.showError = false;
    }
  }

  if (action.type === FormFieldMessagesAction.SETFORMCONTROLID) {
    newState.formControlId = action.payload.formControlId;
  }

  if (action.type === FormFieldMessagesAction.SETMESSAGETARGET) {
    const next = action.payload.messageTarget;
    const current = state.messageTarget;
    // Keep the reference stable when nothing changed so the effect that dispatches
    // this action does not spin (fresh state → re-render → re-dispatch).
    const unchanged =
      (next === undefined && current === undefined) ||
      (next !== undefined &&
        current !== undefined &&
        next.length === current.length &&
        next.every((target, index) => target === current[index]));
    if (unchanged) {
      return state;
    }
    newState.messageTarget = next;
  }

  if (action.type === FormFieldMessagesAction.ADDMEMBERFORMCONTROLID) {
    const {
      payload: { formControlId },
    } = action;

    newState.memberFormControlIds ??= [];

    if (!newState.memberFormControlIds.includes(formControlId)) {
      newState.memberFormControlIds.push(formControlId);
    }
  }

  if (action.type === FormFieldMessagesAction.REMOVEMEMBERFORMCONTROLID) {
    const {
      payload: { formControlId },
    } = action;

    newState.memberFormControlIds = state.memberFormControlIds?.filter(
      (id) => id !== formControlId,
    );
  }

  return updateValidity(newState);
}
